import { useEffect, useMemo, useRef, useState } from "react";
import { LuFolder, LuFolderOpen, LuX } from "react-icons/lu";
import { colors, typography, effects, layout, components } from "../../design/tokens";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import {
  useProjectFilesListFiles,
  useProjectFilesListActions,
  useProjectFilesListStatus,
  ProjectFilesListStatus,
} from "../../core_stores/useProjectFilesListStore";
import { useProjectId } from "../../core_stores/useProjectStore";

interface FileNode {
  name: string;
  path: string;
  isDirectory: boolean;
  children?: FileNode[];
}

interface FlattenedItem {
  node: FileNode;
  level: number;
}

interface FileContextPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (filePath: string) => void;
  searchQuery: string;
  dropdownPosition: { top: number; left: number } | null;
  textareaWidth?: number;
}

export function FileContextPicker(props: FileContextPickerProps) {
  const isCompactView = useIsCompactView();

  if (!props.isOpen) return null;

  return isCompactView ? (
    <FileContextPickerMobile {...props} />
  ) : (
    <FileContextPickerDesktop {...props} />
  );
}

/**
 * Desktop dropdown variant - positioned above textarea
 */
function FileContextPickerDesktop({
  onClose,
  onSelect,
}: FileContextPickerProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const files = useProjectFilesListFiles();
  const filesStatus = useProjectFilesListStatus();
  const { loadForProject } = useProjectFilesListActions();
  const projectId = useProjectId();

  // Internal search state
  const [searchQuery, setSearchQuery] = useState("");

  // Keyboard navigation state
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  // Load files if not already loaded
  useEffect(() => {
    if (projectId && filesStatus === ProjectFilesListStatus.INITIAL) {
      loadForProject(projectId);
    }
  }, [projectId, filesStatus, loadForProject]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Auto-focus search input when opened
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Show loading state
  const isLoading = filesStatus === ProjectFilesListStatus.INITIAL ||
    filesStatus === ProjectFilesListStatus.LOADING_FILES_LIST;

  // Check if we're in search mode
  const isSearching = searchQuery.trim().length > 0;

  // Filter files and folders for search results
  const searchResults = useMemo(() => {
    return filterFilesAndFolders(files, searchQuery);
  }, [files, searchQuery]);

  // Total items in search results for keyboard navigation
  const searchItemsCount = searchResults.files.length + searchResults.folders.length;

  // Filter files based on search query (for tree view when not searching)
  const filteredFiles = filterFiles(files, searchQuery);

  // Build tree from filtered files
  const tree = buildTree(filteredFiles);

  // Flatten tree for keyboard navigation (only visible/expanded items)
  const flattenedItems = useMemo(() => {
    const items: FlattenedItem[] = [];

    const flatten = (nodes: FileNode[], level: number) => {
      for (const node of nodes) {
        items.push({ node, level });
        if (node.isDirectory && expandedPaths.has(node.path) && node.children) {
          flatten(node.children, level + 1);
        }
      }
    };

    flatten(tree, 0);
    return items;
  }, [tree, expandedPaths]);

  // Navigation item count depends on mode
  const navigationItemsCount = isSearching ? searchItemsCount : flattenedItems.length;

  // Reset highlighted index when search query changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery]);

  // Scroll highlighted item into view
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const highlightedElement = scrollContainer.querySelector(`[data-index="${highlightedIndex}"]`);
    if (highlightedElement) {
      highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex]);

  // Toggle directory expanded state
  const toggleExpanded = (path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  // Handle selecting from search results
  const handleSearchSelect = (index: number) => {
    const { files: searchFiles, folders: searchFolders } = searchResults;
    if (index < searchFiles.length) {
      // It's a file
      const path = searchFiles[index];
      const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
      onSelect(normalizedPath);
    } else {
      // It's a folder
      const folderIndex = index - searchFiles.length;
      const path = searchFolders[folderIndex];
      const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
      onSelect(normalizedPath);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const itemCount = isSearching ? navigationItemsCount : flattenedItems.length;
    const maxIndex = Math.max(0, itemCount - 1);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (itemCount > 0) {
          setHighlightedIndex(prev => Math.min(prev + 1, maxIndex));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (itemCount > 0) {
          setHighlightedIndex(prev => Math.max(prev - 1, 0));
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isSearching) {
          // Handle search result selection
          if (navigationItemsCount > 0) {
            handleSearchSelect(highlightedIndex);
          }
        } else {
          // Handle tree navigation
          const item = flattenedItems[highlightedIndex];
          if (item) {
            if (item.node.isDirectory) {
              toggleExpanded(item.node.path);
            } else {
              onSelect(item.node.path);
            }
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (!isSearching) {
          const rightItem = flattenedItems[highlightedIndex];
          if (rightItem?.node.isDirectory && !expandedPaths.has(rightItem.node.path)) {
            toggleExpanded(rightItem.node.path);
          }
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (!isSearching) {
          const leftItem = flattenedItems[highlightedIndex];
          if (leftItem?.node.isDirectory && expandedPaths.has(leftItem.node.path)) {
            toggleExpanded(leftItem.node.path);
          }
        }
        break;
    }
  };

  return (
    <div
      ref={dropdownRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={`absolute ${layout.zIndex.dropdownAbove}
        ${colors.surface.secondary} ${colors.border.primary} border
        ${effects.borderRadius.lg} ${effects.shadow.custom.dropdown}
        overflow-hidden outline-none`}
      style={{
        bottom: '100%',
        left: 0,
        right: 0,
        marginBottom: '8px',
        maxHeight: '400px',
      }}
    >
      {/* Search Input */}
      <div className="px-3 py-2 border-b border-dark-border">
        <div className="relative">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              handleKeyDown(e);
              // Stop propagation to prevent double handling by parent container
              if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
                e.stopPropagation();
              }
            }}
            className="w-full px-3 py-2 pr-8
              bg-dark-surface-secondary
              border border-dark-border
              rounded-md
              text-dark-text text-sm
              placeholder:text-dark-placeholder
              focus:outline-none
              transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2
                text-dark-text-secondary hover:text-dark-text
                transition-colors cursor-pointer p-1 flex items-center justify-center"
              aria-label="Clear search"
            >
              <LuX size={14} />
            </button>
          )}
        </div>
      </div>

      {/* File tree or search results */}
      <div ref={scrollContainerRef} className="overflow-y-auto overflow-x-auto" style={{ maxHeight: '350px' }}>
        {isLoading ? (
          <div className={`px-4 py-8 text-center ${colors.text.secondary} ${typography.size.sm}`}>
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-dark-text-secondary border-t-transparent rounded-full animate-spin" />
              Loading files...
            </div>
          </div>
        ) : isSearching ? (
          /* Search results view */
          <SearchResultsView
            files={searchResults.files}
            folders={searchResults.folders}
            searchQuery={searchQuery}
            highlightedIndex={highlightedIndex}
            onSelect={onSelect}
            onHover={setHighlightedIndex}
          />
        ) : flattenedItems.length === 0 ? (
          <div className={`px-4 py-8 text-center ${colors.text.secondary} ${typography.size.sm}`}>
            No files found
          </div>
        ) : (
          /* Tree view */
          <div className="p-2">
            {flattenedItems.map((item, index) => (
              <FileTreeItemFlat
                key={item.node.path}
                node={item.node}
                level={item.level}
                isHighlighted={index === highlightedIndex}
                isExpanded={expandedPaths.has(item.node.path)}
                dataIndex={index}
                onSelect={onSelect}
                onToggle={toggleExpanded}
                onHover={() => setHighlightedIndex(index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Mobile modal variant - full-screen overlay
 */
function FileContextPickerMobile({
  onClose,
  onSelect,
}: FileContextPickerProps) {
  const files = useProjectFilesListFiles();
  const filesStatus = useProjectFilesListStatus();
  const { loadForProject } = useProjectFilesListActions();
  const projectId = useProjectId();

  // Internal search state
  const [searchQuery, setSearchQuery] = useState("");

  // Load files if not already loaded
  useEffect(() => {
    if (projectId && filesStatus === ProjectFilesListStatus.INITIAL) {
      loadForProject(projectId);
    }
  }, [projectId, filesStatus, loadForProject]);

  // Show loading state
  const isLoading = filesStatus === ProjectFilesListStatus.INITIAL ||
    filesStatus === ProjectFilesListStatus.LOADING_FILES_LIST;

  // Check if we're in search mode
  const isSearching = searchQuery.trim().length > 0;

  // Filter files and folders for search results
  const searchResults = useMemo(() => {
    return filterFilesAndFolders(files, searchQuery);
  }, [files, searchQuery]);

  // Build tree from files (for non-search view)
  const tree = buildTree(files);

  // Handle selection with close
  const handleSelect = (path: string) => {
    onSelect(path);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 ${layout.zIndex.modal} ${components.modal.overlay}`}
        onClick={onClose}
      />

      {/* Modal content */}
      <div
        className={`fixed inset-0 ${layout.zIndex.modal} flex flex-col
          ${colors.surface.secondary}`}
      >
        {/* Header with close button */}
        <div
          className={`flex items-center justify-between px-4 py-3
            border-b ${colors.border.primary}`}
        >
          <span className={`${colors.text.primary} ${typography.size.base} ${typography.weight.semibold}`}>
            Select File
          </span>
          <button
            onClick={onClose}
            className={`${colors.text.secondary} ${colors.hover.brightness} ${effects.transition.default}`}
          >
            <LuX size={24} />
          </button>
        </div>

        {/* Search Input */}
        <div className="px-4 py-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 pr-8
                bg-dark-surface-secondary
                border border-dark-border
                rounded-md
                text-dark-text text-sm
                placeholder:text-dark-placeholder
                focus:outline-none
                transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2
                  text-dark-text-secondary hover:text-dark-text
                  transition-colors cursor-pointer p-1 flex items-center justify-center"
                aria-label="Clear search"
              >
                <LuX size={14} />
              </button>
            )}
          </div>
        </div>

        {/* File tree or search results */}
        <div className="flex-1 overflow-y-auto overflow-x-auto p-2">
          {isLoading ? (
            <div className={`px-4 py-8 text-center ${colors.text.secondary} ${typography.size.sm}`}>
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-dark-text-secondary border-t-transparent rounded-full animate-spin" />
                Loading files...
              </div>
            </div>
          ) : isSearching ? (
            /* Search results view */
            <SearchResultsView
              files={searchResults.files}
              folders={searchResults.folders}
              searchQuery={searchQuery}
              highlightedIndex={-1}
              onSelect={handleSelect}
              onHover={() => {}}
            />
          ) : tree.length === 0 ? (
            <div className={`px-4 py-8 text-center ${colors.text.secondary} ${typography.size.sm}`}>
              No files found
            </div>
          ) : (
            /* Tree view */
            <>
              {tree.map((node) => (
                <FileTreeItem
                  key={node.path}
                  node={node}
                  onSelect={handleSelect}
                  level={0}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Individual file tree item component
 */
function FileTreeItem({
  node,
  level = 0,
  onSelect,
}: {
  node: FileNode;
  level?: number;
  onSelect: (path: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    if (node.isDirectory) {
      setIsOpen(!isOpen);
    } else {
      onSelect(node.path);
    }
  };

  return (
    <div>
      <div
        className={`${colors.hover.background} ${colors.text.primary}
          flex items-center gap-2 cursor-pointer
          py-1 pr-4 ${effects.borderRadius.md}`}
        style={{ paddingLeft: `${level + 0.75}rem` }}
        onClick={handleClick}
      >
        {node.isDirectory ? (
          isOpen ? (
            <LuFolderOpen size={16} />
          ) : (
            <LuFolder size={16} />
          )
        ) : (
          <span className="w-4" /> // Spacer for file items
        )}
        <span className={`${typography.size.sm} ${typography.family.sans}`}>
          {node.name}
        </span>
      </div>
      {node.isDirectory &&
        isOpen &&
        node.children?.map((child) => (
          <FileTreeItem
            key={child.path}
            node={child}
            level={level + 1}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

/**
 * Flat file tree item for keyboard navigation (desktop only)
 * Shows highlighted state for keyboard selection
 */
function FileTreeItemFlat({
  node,
  level,
  isHighlighted,
  isExpanded,
  dataIndex,
  onSelect,
  onToggle,
  onHover,
}: {
  node: FileNode;
  level: number;
  isHighlighted: boolean;
  isExpanded: boolean;
  dataIndex: number;
  onSelect: (path: string) => void;
  onToggle: (path: string) => void;
  onHover: () => void;
}) {
  const handleClick = () => {
    if (node.isDirectory) {
      onToggle(node.path);
    } else {
      onSelect(node.path);
    }
  };

  return (
    <div
      data-index={dataIndex}
      className={`flex items-center gap-2 cursor-pointer
        py-1 pr-4 ${effects.borderRadius.md}
        ${isHighlighted
          ? 'bg-primary-blue text-white'
          : `${colors.hover.background} ${colors.text.primary}`
        }`}
      style={{ paddingLeft: `${level + 0.75}rem` }}
      onClick={handleClick}
      onMouseEnter={onHover}
    >
      {node.isDirectory ? (
        isExpanded ? (
          <LuFolderOpen size={16} />
        ) : (
          <LuFolder size={16} />
        )
      ) : (
        <span className="w-4" />
      )}
      <span className={`${typography.size.sm} ${typography.family.sans}`}>
        {node.name}
      </span>
    </div>
  );
}

/**
 * Search results view - flat list of matching files and folders
 */
function SearchResultsView({
  files,
  folders,
  searchQuery,
  highlightedIndex,
  onSelect,
  onHover,
}: {
  files: string[];
  folders: string[];
  searchQuery: string;
  highlightedIndex: number;
  onSelect: (path: string) => void;
  onHover: (index: number) => void;
}) {
  // Combined list: files first, then folders
  const allItems = [...files, ...folders];

  const renderItem = (path: string, index: number, isFolder: boolean) => {
    const isHighlighted = index === highlightedIndex;
    // Normalize path by removing leading slash
    const normalizedPath = path.startsWith("/") ? path.slice(1) : path;

    return (
      <div
        key={path}
        data-index={index}
        onClick={() => onSelect(normalizedPath)}
        onMouseEnter={() => onHover(index)}
        className={`flex items-center gap-2 cursor-pointer
          py-0.5 px-3 ${effects.borderRadius.md}
          ${isHighlighted
            ? 'bg-primary-blue text-white'
            : `${colors.hover.background} ${colors.text.primary}`
          }`}
      >
        {isFolder ? (
          <LuFolder size={16} className="flex-shrink-0" />
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}
        <span className={`${typography.size.sm} ${typography.family.sans}`}>
          {isHighlighted ? normalizedPath : highlightMatch(normalizedPath, searchQuery)}
        </span>
      </div>
    );
  };

  if (allItems.length === 0) {
    return (
      <div className={`px-4 py-8 text-center ${colors.text.secondary} ${typography.size.sm}`}>
        No files found
      </div>
    );
  }

  return (
    <div className="p-2">
      {/* Files Section */}
      {files.length > 0 && (
        <div className="mb-2">
          <div className={`${typography.size.xs} ${typography.weight.medium} ${colors.text.secondary} uppercase tracking-wider px-3 py-1`}>
            Files
          </div>
          {files.map((file, index) => renderItem(file, index, false))}
        </div>
      )}

      {/* Folders Section */}
      {folders.length > 0 && (
        <div>
          {files.length > 0 && (
            <div className={`border-t ${colors.border.primary} my-2`} />
          )}
          <div className={`${typography.size.xs} ${typography.weight.medium} ${colors.text.secondary} uppercase tracking-wider px-3 py-1`}>
            Folders
          </div>
          {folders.map((folder, index) => renderItem(folder, files.length + index, true))}
        </div>
      )}
    </div>
  );
}

/**
 * Filter files by search query
 */
function filterFiles(files: string[], searchQuery: string): string[] {
  if (!searchQuery) return files;

  const query = searchQuery.toLowerCase();
  return files
    .filter((filePath) => {
      // Match against full path and filename
      const fileName = filePath.split("/").pop() || "";
      return (
        filePath.toLowerCase().includes(query) ||
        fileName.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      // Prioritize matches in filename over full path
      const aFileName = a.split("/").pop() || "";
      const bFileName = b.split("/").pop() || "";
      const aFileMatch = aFileName.toLowerCase().includes(query);
      const bFileMatch = bFileName.toLowerCase().includes(query);

      if (aFileMatch && !bFileMatch) return -1;
      if (!aFileMatch && bFileMatch) return 1;
      return a.localeCompare(b);
    });
}

/**
 * Extract all unique folder paths from file paths
 */
function extractFolders(filePaths: string[]): string[] {
  const folderSet = new Set<string>();

  filePaths.forEach((filePath) => {
    const parts = filePath.split("/").filter(Boolean);
    // Build folder paths incrementally
    let currentPath = "";
    for (let i = 0; i < parts.length - 1; i++) {
      currentPath += "/" + parts[i];
      folderSet.add(currentPath);
    }
  });

  return Array.from(folderSet);
}

/**
 * Filter files and folders based on search query
 * Returns { files: string[], folders: string[] }
 */
function filterFilesAndFolders(
  files: string[],
  searchQuery: string
): { files: string[]; folders: string[] } {
  if (!searchQuery.trim()) {
    return { files: [], folders: [] };
  }

  const query = searchQuery.toLowerCase();

  // Filter matching files (match against filename only, not full path)
  const matchingFiles = files.filter((file) => {
    const filename = file.split("/").pop() || "";
    return filename.toLowerCase().includes(query);
  });

  // Extract all folders from the full file list
  const allFolders = extractFolders(files);

  // Filter matching folders (match against folder name only, not full path)
  const matchingFolders = allFolders.filter((folder) => {
    const folderName = folder.split("/").pop() || "";
    return folderName.toLowerCase().includes(query);
  });

  return { files: matchingFiles, folders: matchingFolders };
}

/**
 * Helper to highlight search term in text
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;

  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);

  return (
    <>
      {before}
      <span className="font-semibold text-blue-400">{match}</span>
      {after}
    </>
  );
}

/**
 * Build hierarchical tree structure from flat file paths
 * (Reused logic from ProjectFilesTree.tsx)
 */
function buildTree(files: string[]): FileNode[] {
  const root: FileNode[] = [];
  const pathMap = new Map<string, FileNode>();

  const sortNodes = (nodes: FileNode[]) => {
    return nodes.sort((a, b) => {
      // If both are directories or both are files, sort by name
      if (a.isDirectory === b.isDirectory) {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      }
      // If one is a directory and one is a file, directory comes first
      return a.isDirectory ? -1 : 1;
    });
  };

  files.forEach((filePath) => {
    const processedPath = filePath.startsWith("/")
      ? filePath.slice(1)
      : filePath;
    const parts = processedPath.split("/");
    let currentPath = "";
    let parentNode = { children: root } as FileNode;

    parts.forEach((part, index) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      const isLast = index === parts.length - 1;

      if (!pathMap.has(currentPath)) {
        const node: FileNode = {
          name: part,
          path: currentPath,
          isDirectory: !isLast,
          children: !isLast ? [] : undefined,
        };
        pathMap.set(currentPath, node);
        parentNode.children?.push(node);
        // Sort the children array after adding a new node
        if (parentNode.children) {
          sortNodes(parentNode.children);
        }
      }
      parentNode = pathMap.get(currentPath)!;
    });
  });

  return sortNodes(root);
}
